import { memo, useState } from "react";
import { type TabularViewData } from "../../../../models/models";
import {
  selectHasTubularResults,
  selectTabularViewData,
  selectVisibleTabularViewColumns,
} from "~/features/search/redux/searchSelectors";
import { useSelector } from "react-redux";
import { useIsMinDesktopSize, useIsMinTabletSize } from "../../../../../../common/hooks/useMediaQuery";
import { MAX_COLUMNS_IN_TABULAR_VIEW } from "../../../../../../utils/constants";
import { ProductComparisonSnackbar } from "~/features/compareProducts/components/ProductComparisonCheckbox";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import {
  calculateTotalTableWidth,
  dataColSizeFn,
  tsmakeActionsColumn,
  tsmakeCompareColumn,
  tsmakeProductColumn,
  tsmakeTechValueColumnFactory,
  tsmakeWeightColumn,
  tsmakeAvailabilityColumn,
} from "~/features/search/components/ProductResults/views/TabularView/TanStackColumnDefs";
import { TableHoverHighlight } from "~/features/search/components/ProductResults/views/TabularView/TableHoverHighlight";
import { useTranslation } from "react-i18next";
import { useComponentSize } from "~/common/hooks/useComponentSize";
import { Alert } from "~/common/mui/Alert/Alert";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";

interface Props {
  maxColumnAmount?: number;
  isSA?: boolean;
}

export const ReduxProductTabularView = memo(({ maxColumnAmount = MAX_COLUMNS_IN_TABULAR_VIEW, isSA }: Props) => {
  const { ref, width } = useComponentSize<HTMLDivElement>();
  const bsaAvailabilityEnabled = useKameleoonFlag(KAMELEOON_FLAGS.bsaAvailability);
  const isMinTablet = useIsMinTabletSize();
  const isDesktop = useIsMinDesktopSize();
  const { t } = useTranslation();

  const fullConfig = useSelector(selectVisibleTabularViewColumns);
  const results = useSelector(selectTabularViewData);

  const sizeCalculator = dataColSizeFn(isDesktop, maxColumnAmount);
  const tsTechValueColumnFactory = tsmakeTechValueColumnFactory(sizeCalculator);
  const config = fullConfig?.slice(0, maxColumnAmount) ?? [];

  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  // Remember to add unitMode from useUnit to dependency if memoized
  const tanstackColumns: ColumnDef<TabularViewData>[] = [
    tsmakeProductColumn(isDesktop),
    ...config.map((col) => tsTechValueColumnFactory(col)),
    tsmakeCompareColumn(),
    tsmakeActionsColumn(),
  ];

  if (isSA) {
    // As per design requirements, the 4th column should be weight (why 4th?!)...
    const startPosition = tanstackColumns.length < 5 ? Math.max(tanstackColumns.length - 3, 0) : 4;
    tanstackColumns.splice(startPosition, 0, tsmakeWeightColumn(sizeCalculator));
  }

  if (bsaAvailabilityEnabled) {
    // As per design requirements, the 2nd column should be availability
    tanstackColumns.splice(1, 0, tsmakeAvailabilityColumn());
  }

  const totalTableWidth = calculateTotalTableWidth(tanstackColumns);
  const isTableLargerThanDesktop = totalTableWidth > width;

  const hasTubularResults = useSelector(selectHasTubularResults);

  let state = undefined;
  let fullWidth = true;
  if (!isMinTablet) {
    state = { columnPinning: { left: ["name"] } };
    fullWidth = false;
  } else if (!isDesktop || isTableLargerThanDesktop) {
    state = { columnPinning: { left: ["name"], right: ["compare", "actions"] } };
    fullWidth = false;
  } else {
    tanstackColumns.slice(0, tanstackColumns.length - 2).forEach((c) => (c.size = undefined));
  }

  let table = (
    <TanStackTable
      data={results}
      columns={tanstackColumns}
      fullwidth={fullWidth}
      variant={"product"}
      state={state}
      stickyHeader={true}
    />
  );

  if (isDesktop) {
    table = <TableHoverHighlight excludeLastCol={true}>{table}</TableHoverHighlight>;
  }

  return (
    <div ref={ref}>
      {!hasTubularResults && (
        <Alert severity={"info"} className={"scha__srp_pdp_tubular_no-results mb-2"}>
          {t("srp.plp.tubularView.noResults")}
        </Alert>
      )}
      {width > 0 && table}
      <ProductComparisonSnackbar open={snackbarOpen} onClose={() => setSnackbarOpen(false)} />
    </div>
  );
});
